import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaFilter, FaSearch } from 'react-icons/fa';
import api from '../../utils/api';

/**
 * Events page component
 */
const Events = () => {
  // State for events data and filtering
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming', 'past', 'all'
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // List of event categories
  const categories = [
    'all',
    'webinar', 
    'conference', 
    'workshop', 
    'seminar', 
    'training'
  ];

  const [eventsData, setEventsData] = useState([]);

  // Format date to display in a user-friendly format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Check if an event is in the past
  const isPastEvent = (dateString) => {
    return new Date(dateString) < new Date();
  };

  // Load events data
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await api.get('/events');
        const data = response?.data?.data || response?.data?.events || response?.data || [];
        const list = Array.isArray(data) ? data : [];
        setEventsData(list);
        setEvents(list);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filter events based on user selections
  const filteredEvents = events
    .filter(event => {
      // Filter by past/upcoming status
      if (filter === 'upcoming') return !isPastEvent(event.date);
      if (filter === 'past') return isPastEvent(event.date);
      return true; // 'all' filter
    })
    .filter(event => {
      // Filter by category
      if (categoryFilter === 'all') return true;
      return event.category === categoryFilter;
    })
    .filter(event => {
      // Filter by search term
      return (event.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
             (event.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
             (typeof event.location === 'string' ? event.location.toLowerCase().includes(searchTerm.toLowerCase()) : false);
    });

  // Calculate featured events (only upcoming)
  const featuredEvents = events
    .filter(event => event.isFeatured && !isPastEvent(event.date))
    .slice(0, 3); // Display at most 3 featured events

  return (
    <>
      <Helmet>
        <title>Events | Meira Educational Events</title>
        <meta name="description" content="Discover upcoming educational events, workshops, seminars, and conferences hosted by Meira." />
      </Helmet>

      {/* Hero section */}
      <div className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">Educational Events</h1>
            <p className="mt-5 max-w-xl mx-auto text-xl text-indigo-100">
              Join us for workshops, webinars, conferences, and more focused on advancing education through innovation.
            </p>
          </div>
        </div>
      </div>

      {/* Featured events section */}
      {featuredEvents.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-12">Featured Events</h2>
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              {featuredEvents.map(event => (
                <div key={event.id} className="flex flex-col rounded-lg shadow-lg overflow-hidden">
                  <div className="flex-shrink-0">
                    <img className="h-48 w-full object-cover" src={event.image} alt={event.title} />
                  </div>
                  <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-primary">
                        <span className="capitalize">{event.category}</span>
                      </p>
                      <a href={`/events/${event.id}`} className="block mt-2">
                        <p className="text-xl font-semibold text-gray-900">{event.title}</p>
                        <p className="mt-3 text-base text-gray-500">{event.description}</p>
                      </a>
                    </div>
                    <div className="mt-6">
                      <div className="flex items-center mb-2">
                        <FaCalendarAlt className="mr-2 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">{formatDate(event.date)}</p>
                      </div>
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="mr-2 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">
                          {typeof event.location === 'string' && event.location}
                          {typeof event.location === 'object' && event.location && (
                            event.location.isVirtual
                              ? 'Online Event'
                              : [event.location.name, event.location.address, event.location.city, event.location.country]
                                  .filter(Boolean)
                                  .join(', ')
                          )}
                        </p>
                      </div>
                      <div className="mt-4">
                        <a
                          href={event.registrationLink}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                        >
                          Register Now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter and search section */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 items-center justify-between">
            <div className="flex space-x-2 items-center">
              <FaFilter className="text-gray-400" />
              <span className="text-gray-700">Filter:</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
              >
                <option value="upcoming">Upcoming Events</option>
                <option value="past">Past Events</option>
                <option value="all">All Events</option>
              </select>
            </div>
            <div className="flex space-x-2 items-center">
              <span className="text-gray-700">Category:</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Events listing */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredEvents.map(event => (
                <div key={event.id} className="flex flex-col rounded-lg shadow-lg overflow-hidden h-full">
                  <div className="flex-shrink-0">
                    <img className="h-48 w-full object-cover" src={event.image} alt={event.title} />
                  </div>
                  <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium text-primary capitalize">{event.category}</p>
                        {isPastEvent(event.date) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Past Event
                          </span>
                        )}
                      </div>
                      <a href={`/events/${event.id}`} className="block mt-2">
                        <p className="text-xl font-semibold text-gray-900">{event.title}</p>
                      </a>
                      <p className="mt-3 text-base text-gray-500">{event.description}</p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center mb-2">
                        <FaCalendarAlt className="mr-2 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">{formatDate(event.date)}</p>
                      </div>
                      <div className="flex items-center mb-2">
                        <FaClock className="mr-2 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">{event.time}</p>
                      </div>
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="mr-2 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">{event.location}</p>
                      </div>
                      {!isPastEvent(event.date) && (
                        <div className="mt-4">
                          <a
                            href={event.registrationLink}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                          >
                            Register Now
                          </a>
                        </div>
                      )}
                      {isPastEvent(event.date) && (
                        <div className="mt-4">
                          <a
                            href={`/events/${event.id}`}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            View Details
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setFilter('all');
                    setCategoryFilter('all');
                    setSearchTerm('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-primary-light py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block">Want to host an event with us?</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-indigo-100">
              Partner with Meira to reach educators and education technology professionals through our event platform.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50"
                >
                  Contact Us
                </a>
              </div>
              <div className="ml-3 inline-flex">
                <a
                  href="/host-event"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-dark hover:bg-primary-darker"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Events;
